/**
 * Single source of truth for Grahak session initialization and identity markers.
 *
 * Initialization avoids redundant bootstrap calls, while the separate session
 * type lets boot and ApiHelper distinguish a known guest from an authenticated
 * user before Redux hydration finishes.
 *
 * Centralizing the read/write here removes 4–5 raw `localStorage` touches
 * scattered across `UserInit`, `AppContainer`, `MyAccount`, `ApiHelper`,
 * and `authResolver`. Tests can spy on this single module instead of the
 * global `localStorage` API.
 */

const INITIALIZED_STORAGE_KEY = 'guestSessionInitialized';
const SESSION_TYPE_STORAGE_KEY = 'grahakSessionType';

const SESSION_TYPE = {
  AUTHENTICATED: 'authenticated',
  GUEST: 'guest',
} as const;

const isBrowser = (): boolean => typeof window !== 'undefined';

const safeLocalStorageGet = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    // Private mode / storage blocked.
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): boolean => {
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // Private mode / quota / storage blocked — silently ignore.
    return false;
  }
};

const safeLocalStorageRemove = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // Private mode / storage blocked — silently ignore.
  }
};

export const isGrahakSessionInitialized = (): boolean =>
  safeLocalStorageGet(INITIALIZED_STORAGE_KEY) === 'true';

export const isGuestSessionMarked = (): boolean =>
  safeLocalStorageGet(SESSION_TYPE_STORAGE_KEY) === SESSION_TYPE.GUEST;

export const isAuthenticatedSessionMarked = (): boolean =>
  safeLocalStorageGet(SESSION_TYPE_STORAGE_KEY) === SESSION_TYPE.AUTHENTICATED;

export const hasKnownGrahakSessionIdentity = (): boolean => {
  const sessionType = safeLocalStorageGet(SESSION_TYPE_STORAGE_KEY);
  return sessionType === SESSION_TYPE.GUEST || sessionType === SESSION_TYPE.AUTHENTICATED;
};

export const markGuestInitialized = (): void => {
  safeLocalStorageRemove(SESSION_TYPE_STORAGE_KEY);
  safeLocalStorageSet(INITIALIZED_STORAGE_KEY, 'true');
  safeLocalStorageSet(SESSION_TYPE_STORAGE_KEY, SESSION_TYPE.GUEST);
};

export const markAuthenticatedSession = (): void => {
  safeLocalStorageRemove(SESSION_TYPE_STORAGE_KEY);
  safeLocalStorageSet(INITIALIZED_STORAGE_KEY, 'true');
  safeLocalStorageSet(SESSION_TYPE_STORAGE_KEY, SESSION_TYPE.AUTHENTICATED);
};

export const clearGuestInitialized = (): void => {
  safeLocalStorageRemove(INITIALIZED_STORAGE_KEY);
  safeLocalStorageRemove(SESSION_TYPE_STORAGE_KEY);
};
